/**
 * HA-150: Upgrade Prompt Analytics Tracking
 *
 * Tracks upgrade prompt impressions and user interactions for product analytics.
 * HIPAA Note: No PHI is logged - only tier/feature/user metadata.
 */

import { FeatureId } from '../config/featureConfig';

/**
 * Types of upgrade prompt analytics events
 */
export type UpgradePromptEventType =
  | 'impression' // Modal or toast displayed
  | 'click_upgrade' // User clicked upgrade/view plans
  | 'click_remind_later' // User clicked remind me later
  | 'dismiss' // User dismissed without action
  | 'toast_impression' // Toast notification displayed
  | 'toast_click_upgrade'; // User clicked upgrade from toast

/**
 * Source of the upgrade prompt
 */
export type UpgradePromptSource = 'modal' | 'toast' | 'guard' | 'api_denial';

/**
 * Upgrade prompt analytics event data
 * HIPAA: Does NOT contain any PHI
 */
export interface UpgradePromptEvent {
  eventType: UpgradePromptEventType;
  source: UpgradePromptSource;
  featureName?: string;
  featureCode?: string;
  featureId?: FeatureId;
  currentTier: string;
  requiredTier?: string;
  userId?: string;
  practiceId?: string;
  role?: string;
  timestamp: string;
  sessionId?: string;
  // Additional context
  denialCode?: string;
  seatUsage?: {
    entitled: number;
    used: number;
  };
}

/**
 * User context for analytics events
 */
export interface AnalyticsUserContext {
  userId?: string;
  practiceId?: string;
  role?: string;
  currentTier: string;
}

// Generate a session ID for correlating events
let sessionId: string | null = null;
const getSessionId = (): string => {
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  return sessionId;
};

// Event buffer for batching
const eventBuffer: UpgradePromptEvent[] = [];
const BUFFER_FLUSH_INTERVAL = 30000; // 30 seconds
const BUFFER_MAX_SIZE = 10;

/**
 * Send events to the analytics backend
 * Currently logs to console; can be extended to send to CloudWatch/Pinpoint/etc.
 */
const flushEvents = async (): Promise<void> => {
  if (eventBuffer.length === 0) return;

  const eventsToSend = [...eventBuffer];

  eventBuffer.length = 0;

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console -- dev-only sink; no analytics backend yet
    console.log('[UpgradeAnalytics] Flushing events:', eventsToSend);
  }

  // TODO: Send to analytics backend when available
  // Options:
  // 1. AWS CloudWatch custom metrics via API
  // 2. AWS Pinpoint for user analytics
  // 3. Third-party analytics (Mixpanel, Amplitude)
  //
  // Example implementation:
  // try {
  //   await fetch('/api/v1/analytics/upgrade-prompts', {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify({ events: eventsToSend }),
  //   });
  // } catch (error) {
  //   console.error('[UpgradeAnalytics] Failed to send events:', error);
  //   // Re-add events to buffer for retry
  //   eventBuffer.push(...eventsToSend);
  // }
};

// Set up periodic flushing
let flushInterval: NodeJS.Timeout | null = null;
const startFlushInterval = (): void => {
  if (flushInterval) return;
  flushInterval = setInterval(flushEvents, BUFFER_FLUSH_INTERVAL);
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flushEvents();
  });
}

/**
 * Track an upgrade prompt event
 */
export const trackUpgradePromptEvent = (
  eventType: UpgradePromptEventType,
  source: UpgradePromptSource,
  context: AnalyticsUserContext,
  additionalData?: Partial<UpgradePromptEvent>
): void => {
  const event: UpgradePromptEvent = {
    eventType,
    source,
    currentTier: context.currentTier,
    userId: context.userId,
    practiceId: context.practiceId,
    role: context.role,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    ...additionalData,
  };

  // Log immediately in development
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console -- dev-only sink; no analytics backend yet
    console.log('[UpgradeAnalytics] Event:', event);
  }

  // Add to buffer
  eventBuffer.push(event);

  // Flush if buffer is full
  if (eventBuffer.length >= BUFFER_MAX_SIZE) {
    flushEvents();
  }

  // Start flush interval if not running
  startFlushInterval();
};

/**
 * Convenience functions for common events
 */
export const trackModalImpression = (
  context: AnalyticsUserContext,
  featureName?: string,
  requiredTier?: string,
  denialCode?: string
): void => {
  trackUpgradePromptEvent('impression', 'modal', context, {
    featureName,
    requiredTier,
    denialCode,
  });
};

export const trackModalUpgradeClick = (
  context: AnalyticsUserContext,
  featureName?: string,
  requiredTier?: string
): void => {
  trackUpgradePromptEvent('click_upgrade', 'modal', context, {
    featureName,
    requiredTier,
  });
};

export const trackModalRemindLater = (
  context: AnalyticsUserContext,
  featureName?: string
): void => {
  trackUpgradePromptEvent('click_remind_later', 'modal', context, {
    featureName,
  });
};

export const trackModalDismiss = (context: AnalyticsUserContext, featureName?: string): void => {
  trackUpgradePromptEvent('dismiss', 'modal', context, {
    featureName,
  });
};

export const trackToastImpression = (context: AnalyticsUserContext, message?: string): void => {
  trackUpgradePromptEvent('toast_impression', 'toast', context, {
    featureName: message,
  });
};

export const trackToastUpgradeClick = (context: AnalyticsUserContext): void => {
  trackUpgradePromptEvent('toast_click_upgrade', 'toast', context);
};

export const trackApiDenial = (
  context: AnalyticsUserContext,
  denialCode: string,
  featureName?: string,
  seatUsage?: { entitled: number; used: number }
): void => {
  trackUpgradePromptEvent('impression', 'api_denial', context, {
    featureName,
    denialCode,
    seatUsage,
  });
};

/**
 * Get current analytics session ID
 */
export const getAnalyticsSessionId = (): string => getSessionId();

/**
 * Force flush all pending events (useful for testing)
 */
export const forceFlush = (): Promise<void> => flushEvents();
